import { XIcon, ZapIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router';

import {
  SidebarWrapper,
  SidebarWrapperContent,
  SidebarWrapperOverflowContainer,
  SidebarWrapperTitle,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import useBrains from '@/hooks/useBrains';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import { setSidebarContent } from '@/redux/sidebar/actions';
import { trackEvent } from '@/segment/segment';
import { actions } from '@/shared/types/permissions';
import { EventName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import { clampPercentage, isUnlimited } from '@/util/limits';

export const AIAgentsDetails = () => {
  const dispatch = useDispatch();
  const slug = useSelector(selectAccountSlug);
  const { t } = useTranslation();
  const { brains: aiAgents, maxBrains: maxAIAgents } = useBrains();

  const canReadBilling = useSelector((state: RootState) =>
    getPermissions(state, 'billing', actions.READ)
  );

  const currentCount = aiAgents?.length || 0;
  const usagePercentage = clampPercentage(currentCount, maxAIAgents);

  const handleClose = () => {
    dispatch(setSidebarContent(null));
  };

  useEscapeKey(handleClose);

  return (
    <SidebarWrapper>
      <SidebarWrapperTitle>
        <h2 className="text-base leading-5 font-medium">{t('try_it.headers.details')}</h2>
        <Button variant="ghost-neutral" size="icon-sm" type="button" onClick={handleClose}>
          <XIcon size={16} />
        </Button>
      </SidebarWrapperTitle>

      <SidebarWrapperOverflowContainer>
        <SidebarWrapperContent>
          <div className="mb-4 flex items-center gap-1">
            <ZapIcon size={16} className="text-foreground-muted" />
            <h3 className="text-sm leading-6 font-semibold">
              {t('ai_agents.details.usage.title')}
            </h3>
          </div>

          <div className="max-w-lg space-y-1">
            <div className="flex items-center justify-between gap-3">
              {!isUnlimited(maxAIAgents) && (
                <Progress className="max-w-[75%]" value={usagePercentage} />
              )}
              <p className="text-xs leading-5 font-medium whitespace-nowrap">
                {isUnlimited(maxAIAgents)
                  ? t('table.resource.ai_agent', { count: currentCount })
                  : t('ai_agents.details.out_of', { 0: currentCount, 1: maxAIAgents })}
              </p>
            </div>

            {!isUnlimited(maxAIAgents) && (
              <p className="text-xs leading-5 text-foreground-muted">
                {t('ai_agents.details.usage.description')}
                {', '}
                {canReadBilling ? (
                  <Link
                    to={`/${slug}/account/billing`}
                    className="font-medium text-surface-button-primary"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      trackEvent(EventName.ClickUpgradePlan);
                    }}
                  >
                    {t('billing.upgrade_plan').toLowerCase()}
                  </Link>
                ) : (
                  <>{t('billing.upgrade_plan').toLowerCase()}</>
                )}
              </p>
            )}
          </div>
        </SidebarWrapperContent>
      </SidebarWrapperOverflowContainer>
    </SidebarWrapper>
  );
};
